import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import currency from 'currency.js';
import type { JournalReportMoreInfo } from 'donor-elf-web/components/journal/report/more-info-modal';
import type { JournalIntervarsityReportRouteModel } from 'donor-elf-web/routes/funds/fund/journals/journal/report/intervarsity';
import { storageFor } from 'ember-local-storage';
import Highcharts from 'highcharts';
import moment from 'moment';

export default class JournalReportIntervarsityController extends Controller {
  declare model: JournalIntervarsityReportRouteModel;

  @service declare formatAmountService: any;
  @storageFor('local-settings') localSettings: any;

  @tracked moreInfoModalInfo?: JournalReportMoreInfo;
  @tracked showLastIntervalCount = 1;
  sameHeightElementGroups: any = {};

  get showAllIntervals() {
    return (
      this.showLastIntervalCount === 0 ||
      this.showLastIntervalCount === this.totalIntervalCount
    );
  }

  get firstGoal() {
    return this.model.reportIntervarsity.goals[0];
  }

  get showLastOptions() {
    let { totalIntervalCount } = this;
    let options = [
      {
        label: 'All Weeks',
        val: 0,
      },
    ];

    for (let index = 1; index <= totalIntervalCount; index++) {
      let label = index === 1 ? 'Last Week' : `Last ${index} Weeks`;
      options.push({
        label,
        val: index,
      });
    }

    return options;
  }

  get totalIntervalCount() {
    return this.model.reportIntervarsity.intervalData.length;
  }

  @cached
  get selectedIntervalItems() {
    if (this.showLastIntervalCount === 0) {
      return this.model.reportIntervarsity.intervalData;
    } else {
      return this.model.reportIntervarsity.intervalData.slice(
        this.model.reportIntervarsity.intervalData.length -
          this.showLastIntervalCount,
      );
    }
  }

  get selectedIntervalYesDecisions() {
    let yesDecisions: any = [];
    this.selectedIntervalItems.forEach((item) => {
      item.decisionInfos.forEach((decisionInfo) => {
        if (decisionInfo.journalDecision.yesNo) {
          yesDecisions.push(decisionInfo);
        }
      });
    });
    return yesDecisions;
  }

  @cached
  get overviewTotals() {
    let { selectedIntervalItems, firstGoal } = this;
    return {
      engagementsTotal: selectedIntervalItems
        .map((x) => x.completedTaskCounts.engagement)
        .reduce((a, b) => {
          return a + b;
        }, 0),
      asksMadeTotal: selectedIntervalItems
        .map((x) => x.completedTaskCounts.ask)
        .reduce((a, b) => {
          return a + b;
        }, 0),
      recommendationAsksMadeTotal: selectedIntervalItems
        .map((x) => x.completedTaskCounts.recommendationAsk)
        .reduce((a, b) => {
          return a + b;
        }, 0),
      followupsTotal: selectedIntervalItems
        .map((x) => x.completedTaskCounts.followup)
        .reduce((a, b) => {
          return a + b;
        }, 0),
      closedAsksTotal: selectedIntervalItems
        .map((x) => x.decisionInfos.length)
        .reduce((a, b) => {
          return a + b;
        }, 0),
      decisionAmountTotal: selectedIntervalItems
        .map((x) => x.goalsData[firstGoal.id].decisionNewAmount)
        .reduce((a, b) => {
          return currency(a).add(b).value;
        }, 0),
    };
  }

  // returns the sum of decisions made (excludes multiple decisions from same contact)
  // format: { yes: 1, no: 1, total: 1 }
  @cached
  get allDecisionCounts() {
    let contactHasDecision: any = {};
    let decisions = { yes: 0, no: 0 };
    this.model.reportIntervarsity.intervalData.forEach((item) => {
      item.decisionInfos.forEach((decisionInfo) => {
        if (
          contactHasDecision[decisionInfo.journalDecision.journalContactId] ==
          null
        ) {
          if (decisionInfo.journalDecision.yesNo) {
            decisions.yes += 1;
          } else {
            decisions.no += 1;
          }
          contactHasDecision[decisionInfo.journalDecision.journalContactId] =
            true;
        }
      });
    });
    return decisions;
  }

  @action
  templateDidInsert() {
    this.sameHeightElementGroups = {};
    setTimeout(() => {
      this.showLastIntervalCount = Math.min(
        this.localSettings.get('journalShowLastIntervalCount'),
        this.totalIntervalCount,
      );
    });
  }

  @action
  handleShowLastIntervalCountChanged(val: number) {
    if (this.showLastIntervalCount !== val) {
      this.showLastIntervalCount = val;
      this.localSettings.set(
        'journalShowLastIntervalCount',
        this.showLastIntervalCount,
      );
    }
  }

  @action
  makeElementsSameHeight(groupName: string, el: HTMLElement) {
    if (this.sameHeightElementGroups[groupName] == null) {
      this.sameHeightElementGroups[groupName] = [];
    }
    let elementGroup = this.sameHeightElementGroups[groupName];
    elementGroup.push(el);

    if (elementGroup.length === 2) {
      let maxHeight = elementGroup.reduce((max: number, el: HTMLElement) => {
        el.style.height = '';
        return Math.max(max, el.offsetHeight);
      }, 0);
      elementGroup.forEach((el: HTMLElement) => {
        el.style.height = `${maxHeight}px`;
      });
    }
  }

  @action
  closeMoreInfoModal() {
    this.moreInfoModalInfo = undefined;
  }

  @action
  activityDivInserted(e: HTMLElement) {
    Highcharts.chart(e, this.activityChart);
  }

  get activityChart() {
    let { selectedIntervalItems } = this;
    let series = [];
    series.push({
      type: 'column',
      name: 'Engagements',
      color: '#0b68a1',
      events: {
        click: (event: any) => {
          this.moreInfoModalInfo = {
            taskIds: event.point.taskIds,
            heading: 'Engagements',
            dateFormatted: event.point.dateFormatted,
          };
        },
      },
      data: selectedIntervalItems.map((item) => {
        return {
          y: item.completedTaskCounts.engagement,
          taskIds: item.completedTaskCounts.engagementTaskIds,
          dateFormatted: item.dateFormatted,
        };
      }),
    });
    series.push({
      type: 'column',
      name: 'Asks Made',
      color: '#f0ad4e',
      events: {
        click: (event: any) => {
          this.moreInfoModalInfo = {
            taskIds: event.point.taskIds,
            heading: 'Asks',
            dateFormatted: event.point.dateFormatted,
          };
        },
      },
      data: selectedIntervalItems.map((item) => {
        return {
          y: item.completedTaskCounts.ask,
          taskIds: item.completedTaskCounts.askTaskIds,
          dateFormatted: item.dateFormatted,
        };
      }),
    });
    series.push({
      type: 'column',
      name: 'Follow Ups',
      color: '#aaafb4',
      events: {
        click: (event: any) => {
          this.moreInfoModalInfo = {
            taskIds: event.point.taskIds,
            heading: 'Follow Ups',
            dateFormatted: event.point.dateFormatted,
          };
        },
      },
      data: selectedIntervalItems.map((item) => {
        return {
          y: item.completedTaskCounts.followup,
          taskIds: item.completedTaskCounts.followupTaskIds,
          dateFormatted: item.dateFormatted,
        };
      }),
    });

    return {
      chart: {
        height: 200,
      },
      title: {
        text: '',
      },
      credits: {
        enabled: false,
      },
      xAxis: {
        categories: selectedIntervalItems.map((x) => x.date),
        labels: {
          formatter: function (): any {
            // @ts-expect-error
            return moment(this.value).format('M/D');
          },
        },
        min: 0,
        max: selectedIntervalItems.length - 1,
      },
      yAxis: {
        min: 0,
        title: {
          text: null,
        },
      },
      tooltip: {
        headerFormat: '',
        pointFormat: '{series.name}: {point.y}',
        style: {
          fontSize: '14px',
        },
      },
      legend: {
        enabled: false,
      },
      series,
    };
  }
}
